/**
 * affiliate-commissions-page.tsx — Platform → Tiếp thị liên kết → Hoa hồng. Route `/platform/affiliate/commissions`.
 * Sổ hoa hồng (lọc trạng thái / loại), "Thu hồi" một dòng (lý do + nhật ký — dùng khi gian lận; KHÔNG có hoàn tiền đơn).
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatVnd } from '@/lib/format-money'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import { commissionStatusLabel, formatSignedVnd } from '@/lib/affiliate'
import { useAdminCommissions, useVoidCommission, type AdminCommission } from '@/hooks/use-platform-affiliate'
import { FilterChips, fmtDate, ReasonDialog } from './affiliate-shared'

export function AffiliateCommissionsPage() {
  const [status, setStatus] = useState('')
  const [kind, setKind] = useState('')
  const [page, setPage] = useState(1)
  const [voiding, setVoiding] = useState<AdminCommission | null>(null)
  const { data, isLoading } = useAdminCommissions({ status, kind, partnerId: '', page })
  const voidC = useVoidCommission()

  const columns: Column<AdminCommission>[] = [
    { key: 'date', header: 'Ngày', cell: (c) => fmtDate(c.orderPaidAt ?? c.createdAt) },
    { key: 'partner', header: 'Đối tác', cell: (c) => <span>{c.partnerName} <span className="font-mono text-xs text-muted-foreground">({c.partnerCode})</span></span> },
    {
      key: 'what', header: 'Nguồn',
      cell: (c) => c.kind === 'adjustment'
        ? <span>Điều chỉnh tay: {c.reason}</span>
        : (
          <div>
            {c.orgId ? <Link className="hover:text-primary" to={`/platform/companies/${c.orgId}`}>{c.customer}</Link> : c.customer}
            <p className="font-mono text-[11px] text-muted-foreground">đơn {c.orderId}</p>
          </div>
        ),
    },
    { key: 'base', header: 'Cơ sở (trước VAT)', align: 'right', cell: (c) => (c.kind === 'commission' ? formatVnd(c.baseAmount) : '—') },
    {
      key: 'rate', header: 'Mức',
      cell: (c) => c.rateType === 'percent' ? `${c.rateValue}%${c.tier ? ` (${c.tier})` : ''}` : c.rateType === 'fixed' ? `Cố định${c.tier ? ` (${c.tier})` : ''}` : '—',
    },
    { key: 'amount', header: 'Hoa hồng', align: 'right', cell: (c) => <span className={cn('font-medium', c.amount < 0 && 'text-destructive')}>{formatSignedVnd(c.amount)}</span> },
    {
      key: 'status', header: 'Trạng thái',
      cell: (c) => (
        <div>
          <Badge variant={c.status === 'available' ? 'success' : c.status === 'void' ? 'destructive' : 'warning'}>{commissionStatusLabel(c.status)}</Badge>
          {c.status === 'holding' && <p className="text-xs text-muted-foreground">đến {fmtDate(c.holdUntil)}</p>}
          {c.status === 'void' && <p className="max-w-[200px] truncate text-xs text-muted-foreground" title={c.voidReason ?? ''}>{c.voidReason}</p>}
        </div>
      ),
    },
    {
      key: 'act', header: '', align: 'right',
      cell: (c) => c.status !== 'void' && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setVoiding(c)}>Thu hồi</Button>,
    },
  ]

  return (
    <div className="space-y-5">
      <PageHeader
        title="Sổ hoa hồng"
        description="Mỗi đơn đã thanh toán của khách được giới thiệu tạo tối đa một dòng hoa hồng. Không hoàn tiền đơn nên hoa hồng không tự trừ ngược."
      />
      <div className="flex flex-wrap items-center gap-3">
        <FilterChips value={status} onChange={(v) => { setStatus(v); setPage(1) }} options={[['', 'Mọi trạng thái'], ['holding', 'Đang giữ'], ['available', 'Có thể rút'], ['void', 'Đã thu hồi']]} />
        <FilterChips value={kind} onChange={(v) => { setKind(v); setPage(1) }} options={[['', 'Mọi loại'], ['commission', 'Từ đơn'], ['adjustment', 'Điều chỉnh']]} />
        {data && <span className="ml-auto text-sm text-muted-foreground">Tổng theo bộ lọc: <b className="text-foreground">{formatSignedVnd(data.sumAmount)}</b></span>}
      </div>
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(c) => c.id} emptyTitle="Chưa có hoa hồng" />
      {data && data.total > data.limit && <Pagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />}
      <ReasonDialog
        open={!!voiding}
        title="Thu hồi hoa hồng"
        description={voiding ? `Dòng ${formatSignedVnd(voiding.amount)} của ${voiding.partnerName} sẽ bị loại khỏi số dư (có thể làm số dư âm nếu đã rút). Chỉ dùng khi gian lận / sai sót.` : undefined}
        confirmLabel="Thu hồi"
        destructive
        pending={voidC.isPending}
        onClose={() => setVoiding(null)}
        onConfirm={(reason) => voiding && voidC.mutate({ id: voiding.id, reason }, {
          onSuccess: () => { toast.success('Đã thu hồi'); setVoiding(null) },
          onError: (e) => toast.error(platformApiError(e)),
        })}
      />
    </div>
  )
}
